import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSun, FaMoon } from 'react-icons/fa';
import { useTheme } from '../context/ThemeContext';

const ThemeLanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [showLanguages, setShowLanguages] = useState(false);
  const dropdownRef = useRef(null);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowLanguages(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
    setShowLanguages(false);
    
    // Update document direction for RTL languages
    if (lng === 'ar') {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Theme Toggle - Smaller */}
      <button
        onClick={toggleTheme}
        className="p-1.5 rounded-lg bg-gray-200 dark:bg-dark-700 hover:bg-gray-300 dark:hover:bg-dark-600 transition-colors duration-300"
        aria-label="Toggle theme"
      >
        {theme === 'dark' ? (
          <FaSun className="text-sm text-yellow-400" />
        ) : (
          <FaMoon className="text-sm text-gray-700" />
        )}
      </button>

      {/* Language Selector - Flag Only */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setShowLanguages(!showLanguages)}
          className="flex items-center justify-center p-1.5 rounded-lg bg-gray-200 dark:bg-dark-700 hover:bg-gray-300 dark:hover:bg-dark-600 transition-colors duration-300 min-w-[36px]"
          aria-label="Select language"
        >
          <span className="text-xl">{currentLanguage.flag}</span>
        </button>

        {/* Language Dropdown */}
        {showLanguages && (
          <div className="absolute right-0 mt-2 w-44 bg-white dark:bg-dark-700 rounded-lg shadow-xl border border-gray-200 dark:border-dark-600 overflow-hidden z-50">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors duration-200 ${
                  i18n.language === lang.code ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                }`}
              >
                <span className="text-xl">{lang.flag}</span>
                <span className={`text-sm font-medium ${
                  i18n.language === lang.code 
                    ? 'text-primary dark:text-primary-400' 
                    : 'text-gray-700 dark:text-gray-300'
                }`}>
                  {lang.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ThemeLanguageSwitcher;
